import React from 'react'
import { Switch, Skeleton, Dropdown, Alert } from 'antd'
import { Horizontal, Vertical, Texto, GraviGrid, MaterialIcon, BBDTag } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { useManagementDashboardData } from './hooks/useManagementDashboardData'
import { useSharedHome } from '../context/SharedHomeContext'
import { BackgroundRefreshSpinner } from '../components/BackgroundRefreshSpinner'
import { StatusAggregation } from '../components/StatusAggregation'
import { MetricCard } from '../components/MetricCard'
import { DeltaDisplay } from '../Launchpad/components/ui/DeltaDisplay'
import { useNavigate } from 'react-router-dom'
import { formatMetricNumber, formatDollarMetric } from '../Launchpad/components/utils/formatMetricNumber'
import { useSyncedLocalStorage } from '@hooks/settings'
import { handleNavigation } from '../utils/navigationHelpers'
import dayjs from 'dayjs'
import { getPastDueColumnDefs, top10ColumnDefs, latePaymentColumnDefs } from './columnDefs'

function Management() {
  const [supplierFilter, setSupplierFilter] = React.useState<string | undefined>(undefined)

  // Persist groupByDate and selectedSupplier in localStorage and sync with server
  const [groupByDate, setGroupByDate] = useSyncedLocalStorage('management_dashboard_group_by_date', false)
  const [selectedSupplier, setSelectedSupplier] = useSyncedLocalStorage<string | undefined>(
    'management_dashboard_selected_supplier',
    ''
  )

  const [supplierDropdownOpen, setSupplierDropdownOpen] = React.useState(false)
  const navigate = useNavigate()

  const {
    data: { matches, isLoading, isBackgroundRefreshing, trends: trendsData, hasError, error },
    assignedToMe,
    setAssignedToMe,
    dateRange,
  } = useSharedHome()

  function createMatchViewUrl(baseUrl: string, includeAssigned = true) {
    // Note: Date ranges are handled by Match View's internal state management
    // Passing them via URL creates unnecessary clutter and persistence issues
    if (includeAssigned) {
      const connector = baseUrl.includes('?') ? '&' : '?'
      const value = assignedToMe ? 'true' : 'false'
      return `${baseUrl}${connector}assigned_to_me=${value}`
    }
    return baseUrl
  }

  // Calculate management metrics from shared data
  const {
    data: calculatedData,
    supplierOptions,
    error: managementError,
  } = useManagementDashboardData({
    supplier_id: supplierFilter,
    matches: matches,
    groupByDate: groupByDate,
    selectedSupplier: selectedSupplier,
    trends: trendsData,
  })

  // Combined error state from both SharedHome and Management hooks
  const hasAnyError = hasError || !!managementError

  // State validation to ensure selectedSupplier never becomes undefined
  React.useEffect(() => {
    if (selectedSupplier === undefined || selectedSupplier === null) {
      console.warn('selectedSupplier became undefined/null, resetting to empty string')
      setSelectedSupplier('')
    }
  }, [selectedSupplier, setSelectedSupplier])

  const { data, hasValidTrendData } = React.useMemo(() => {
    // Always use API trend data when available, even for single day
    if (trendsData && trendsData.length > 0) {
      // Sort trends by date to ensure proper oldest/newest identification
      const sortedTrends = [...trendsData].sort(
        (a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime()
      )

      const oldestSnapshot = sortedTrends[0]
      const latestSnapshot = sortedTrends[sortedTrends.length - 1]
      const canCalculateTrends = trendsData.length > 1

      function calculateTrend(oldValue: number, newValue: number): number {
        if (!canCalculateTrends || oldValue === 0) return newValue > 0 ? 100 : 0

        const trend = ((newValue - oldValue) / oldValue) * 100

        // Cap trends at -100% to 100% for display purposes
        if (trend > 100) return 100
        if (trend < -100) return -100

        return trend
      }

      const hasData =
        latestSnapshot.auto_match_percentage > 0 ||
        latestSnapshot.total_past_due > 0 ||
        latestSnapshot.total_open_matches > 0

      return {
        data: {
          ...calculatedData,
          // Priority: Use API values for key metrics
          automatch_percentage: latestSnapshot.auto_match_percentage,
          automatch_trend: canCalculateTrends
            ? calculateTrend(oldestSnapshot.auto_match_percentage, latestSnapshot.auto_match_percentage)
            : 0,
          auto_approve_percentage: latestSnapshot.auto_match_percentage,
          auto_approve_trend: canCalculateTrends
            ? calculateTrend(oldestSnapshot.auto_match_percentage, latestSnapshot.auto_match_percentage)
            : 0,
          past_due_balance: latestSnapshot.total_past_due,
          past_due_balance_trend: canCalculateTrends
            ? calculateTrend(oldestSnapshot.total_past_due, latestSnapshot.total_past_due)
            : 0,
          open_items_count: latestSnapshot.total_open_matches,
          open_items_trend: canCalculateTrends
            ? calculateTrend(oldestSnapshot.total_open_matches, latestSnapshot.total_open_matches)
            : 0,
          total_orphan_bols: latestSnapshot.total_orphan_bols,
          orphan_bols_trend: canCalculateTrends
            ? calculateTrend(oldestSnapshot.total_orphan_bols, latestSnapshot.total_orphan_bols)
            : 0,
        },
        hasValidTrendData: hasData && canCalculateTrends,
      }
    }

    return { data: calculatedData, hasValidTrendData: false }
  }, [trendsData, calculatedData])

  const pastDueColumnDefs = React.useMemo(() => getPastDueColumnDefs(groupByDate), [groupByDate])

  const sparkData = React.useMemo(() => {
    if (trendsData && trendsData.length > 0) {
      // Sort trends by date for consistent chart data
      const sortedTrends = [...trendsData].sort(
        (a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime()
      )

      return sortedTrends.map((snapshot) => ({
        // Include all metrics in centralized sparkData
        past_due: snapshot.total_past_due,
        open_items: snapshot.total_open_matches,
        auto_approve: snapshot.auto_match_percentage * 100, // Convert to percentage
        orphan_bols: snapshot.total_orphan_bols,
        date: (() => {
          // Handle mixed UTC/non-UTC date formats
          const dateStr = snapshot.date
          if (!dateStr) return 'Unknown'

          let result
          if (dateStr.endsWith('Z')) {
            // UTC date - parse as UTC
            result = dayjs.utc(dateStr).format('MMM D, YYYY')
          } else {
            // Non-UTC date - parse as is (don't convert to UTC)
            result = dayjs(dateStr).format('MMM D, YYYY')
          }

          return result
        })(),
      }))
    }
    return []
  }, [trendsData])

  // Calculate total matches in the current date range for percentage calculations
  const totalMatches = matches.length

  const statusBarData = [
    {
      name: 'Overdue',
      value: data?.statusBreakdown?.overDue?.count || 0,
      percentage: totalMatches > 0 ? ((data?.statusBreakdown?.overDue?.count || 0) / totalMatches) * 100 : 0,
      color: '#DC3545',
    },
    {
      name: 'On Hold',
      value: data?.statusBreakdown?.onHold?.count || 0,
      percentage: totalMatches > 0 ? ((data?.statusBreakdown?.onHold?.count || 0) / totalMatches) * 100 : 0,
      color: '#FD7E14',
    },
    {
      name: 'Open',
      value: data?.statusBreakdown?.open?.count || 0,
      percentage: totalMatches > 0 ? ((data?.statusBreakdown?.open?.count || 0) / totalMatches) * 100 : 0,
      color: '#FFC107',
    },
  ]

  const pastDueData = data?.past_due || {}
  const totalAmount = (pastDueData.day_3 || 0) + (pastDueData.day_7 || 0) + (pastDueData.day_20 || 0)
  const maxAmount = Math.max(pastDueData.day_3 || 0, pastDueData.day_7 || 0, pastDueData.day_20 || 0, 1)

  return (
    <>
      <Horizontal
        justifyContent='space-between'
        alignItems='center'
        bg='default'
        border='bottom'
        style={{ padding: 12 }}
      >
        <Horizontal gap={10} alignItems='center'>
          <Texto weight='bold' category='h4'>
            Management Dashboard
          </Texto>
          <BackgroundRefreshSpinner isRefreshing={isBackgroundRefreshing} />
        </Horizontal>
        <Horizontal alignItems='center' gap={6}>
          <Texto category='label' weight='bold'>
            ASSIGNED TO ME
          </Texto>
          <Switch size='small' checked={assignedToMe || false} onChange={setAssignedToMe} />
        </Horizontal>
      </Horizontal>

      {hasAnyError && (
        <Alert
          message='Failed to load some dashboard data'
          type='warning'
          showIcon
          closable
          style={{ margin: '16px' }}
        />
      )}

      <Horizontal gap={'6px'} style={{ height: 'calc(100vh - 120px)', background: '#e8e8e8' }}>
        {/* Left Column - Past Due and Overview */}
        <Vertical
          style={{
            flex: 1,
            minWidth: '610px',
            height: '100%',
            overflow: 'visible',
            gap: '24px',
          }}
        >
          {/* Past Due Section */}
          <Vertical style={{ background: '#fff', borderRadius: '4px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.07)' }}>
            <Horizontal
              justifyContent='space-between'
              alignItems='center'
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid #E5E7EB',
                overflow: 'visible',
              }}
            >
              <Texto category='p2' weight='bold'>
                Past Due
              </Texto>
            </Horizontal>

            {/* Outstanding Balance with Trend Pill */}
            <Vertical style={{ overflow: 'visible', paddingBottom: '12px' }}>
              <Horizontal alignItems='center' gap={8} style={{ padding: '24px 24px 0' }}>
                <Texto style={{ fontSize: '12px', color: '#6C757D' }}>Outstanding Total ($)</Texto>
              </Horizontal>
              {isLoading ? (
                <Horizontal alignItems='center' gap={8} style={{ padding: '0 24px' }}>
                  <Skeleton.Input active style={{ width: 150, height: 32 }} />
                  <Skeleton.Input active style={{ width: 60, height: 20 }} />
                </Horizontal>
              ) : (
                <Horizontal alignItems='center' gap={8} style={{ padding: '0 24px' }}>
                  <Texto weight='bold' style={{ fontSize: '32px', color: '#212529', lineHeight: '32px' }}>
                    {formatDollarMetric(data?.past_due?.total || 0)}
                  </Texto>
                  <DeltaDisplay value={data?.past_due?.trend || 7} invertColors={true} />
                </Horizontal>
              )}

              {/* Progress Bars */}
              <Horizontal
                justifyContent='space-between'
                alignItems='center'
                style={{ marginTop: '24px', padding: '0 24px', display: 'block' }}
              >
                {isLoading ? (
                  <>
                    <Skeleton.Input active style={{ width: '100%', height: 16, marginBottom: 8 }} />
                    <Skeleton.Input active style={{ width: '100%', height: 3, marginBottom: 12 }} />
                    <Skeleton.Input active style={{ width: '100%', height: 16, marginBottom: 8 }} />
                    <Skeleton.Input active style={{ width: '100%', height: 3, marginBottom: 12 }} />
                    <Skeleton.Input active style={{ width: '100%', height: 16, marginBottom: 8 }} />
                    <Skeleton.Input active style={{ width: '100%', height: 3, marginBottom: 12 }} />
                  </>
                ) : (
                  <>
                    <Horizontal justifyContent='space-between' alignItems='center'>
                      <Texto style={{ fontSize: '14px', lineHeight: '16px', color: '#212529' }}>Total 3 Day</Texto>
                      <Texto style={{ fontSize: '14px', lineHeight: '16px', color: '#6C757D' }}>
                        {formatDollarMetric(pastDueData.day_3 || 0)}
                      </Texto>
                    </Horizontal>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#E8E8E8',
                        borderRadius: '4px',
                        marginTop: '5px',
                        marginBottom: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${((pastDueData.day_3 || 0) / totalAmount) * 100}%`,
                          height: '100%',
                          backgroundColor: '#3B82F6',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>

                    <Horizontal justifyContent='space-between' alignItems='center'>
                      <Texto
                        style={{ fontSize: '14px', lineHeight: '16px', letterSpacing: '0.44px', color: '#212529' }}
                      >
                        Total 7 Day
                      </Texto>
                      <Texto
                        style={{ fontSize: '14px', lineHeight: '16px', letterSpacing: '0.44px', color: '#6C757D' }}
                      >
                        {formatDollarMetric(pastDueData.day_7 || 0)}
                      </Texto>
                    </Horizontal>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#E8E8E8',
                        borderRadius: '4px',
                        marginTop: '5px',
                        marginBottom: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${((pastDueData.day_7 || 0) / totalAmount) * 100}%`,
                          height: '100%',
                          backgroundColor: '#3B82F6',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <Horizontal justifyContent='space-between' alignItems='center'>
                      <Texto
                        style={{ fontSize: '14px', lineHeight: '16px', letterSpacing: '0.44px', color: '#212529' }}
                      >
                        Total 20 Day
                      </Texto>
                      <Texto
                        style={{ fontSize: '14px', lineHeight: '16px', letterSpacing: '0.44px', color: '#6C757D' }}
                      >
                        {formatDollarMetric(pastDueData.day_20 || 0)}
                      </Texto>
                    </Horizontal>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#E8E8E8',
                        borderRadius: '4px',
                        marginTop: '5px',
                        marginBottom: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${((pastDueData.day_20 || 0) / totalAmount) * 100}%`,
                          height: '100%',
                          backgroundColor: '#3B82F6',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </>
                )}
              </Horizontal>

              {/* Past Due Overview Section */}
              <Horizontal
                justifyContent='space-between'
                alignItems='center'
                style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #E5E7EB',
                  overflow: 'visible',
                }}
                bg='#F8F9FA'
              >
                <Texto category='p2' weight='bold'>
                  Past Due Overview
                </Texto>
                <Horizontal gap={8} alignItems='center'>
                  <Texto
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      lineHeight: '12px',
                      color: '#495057',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    GROUP BY DATE{' '}
                    <Switch
                      checked={groupByDate}
                      onChange={(value) => {
                        setGroupByDate(value)
                      }}
                      size='small'
                      style={{ marginLeft: '8px', marginRight: '24px' }}
                    />
                  </Texto>
                  <Dropdown
                    open={supplierDropdownOpen}
                    onOpenChange={setSupplierDropdownOpen}
                    menu={{
                      items: [
                        { key: '', label: 'All Suppliers' },
                        ...(supplierOptions || []).map((supplier) => ({
                          key: supplier.supplier_name,
                          label: supplier.supplier_name,
                          onClick: () => {
                            const newValue = supplier.supplier_name === selectedSupplier ? '' : supplier.supplier_name
                            setSelectedSupplier(newValue)
                            setSupplierDropdownOpen(false)
                          },
                        })),
                      ],
                      onClick: (e) => {
                        if (e.key === '') {
                          setSelectedSupplier('')
                          setSupplierDropdownOpen(false)
                        }
                      },
                    }}
                    trigger={['click']}
                  >
                    <Horizontal
                      alignItems='center'
                      bg='#E8E8E8'
                      style={{ padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      <Texto style={{ fontSize: '12px', color: '#4B5563', fontWeight: 700 }}>
                        {!selectedSupplier || selectedSupplier === '' ? 'All Suppliers' : selectedSupplier}
                      </Texto>
                      <MaterialIcon
                        icon={supplierDropdownOpen ? 'expand_less' : 'expand_more'}
                        size={16}
                        style={{ color: '#4B5563' }}
                      />
                    </Horizontal>
                  </Dropdown>
                </Horizontal>
              </Horizontal>

              {/* Table */}
              <div
                style={{
                  flex: 1,
                  minHeight: '200px',
                  borderRadius: '0px',
                  overflow: 'hidden',
                  margin: '-2px',
                }}
              >
                <GraviGrid
                  key={`past-due-${groupByDate ? 'date' : 'supplier'}-${selectedSupplier || 'all'}`}
                  rowData={data?.past_due_overview || []}
                  loading={isLoading}
                  agPropOverrides={{
                    columnDefs: pastDueColumnDefs,
                    headerHeight: 36,
                    rowHeight: 32,
                    domLayout: 'normal',
                    suppressHorizontalScroll: false,
                    animateRows: false,
                    enableCellTextSelection: true,
                    suppressMovableColumns: true,
                    suppressRowTransform: true,
                    // Always hide row group panel
                    rowGroupPanelShow: 'never',
                    // Global setting to suppress aggregation function in headers
                    suppressAggFuncInHeader: true,
                    // Enable aggregation
                    enableValue: true,
                    suppressAggAtRootLevel: false,
                    // Row grouping configuration when groupByDate is true
                    ...(groupByDate && {
                      groupDisplayType: 'groupRows',
                      autoGroupColumnDef: {
                        headerName: '',
                        width: 110,
                        minWidth: 110,
                        cellRendererParams: {
                          suppressCount: true,
                          padding: 0,
                        },
                        pinned: 'left',
                        cellStyle: { fontSize: '14px', color: '#212529', fontWeight: '600' },
                      },
                      groupDefaultExpanded: 0,
                      suppressDragLeaveHidesColumns: true,
                      suppressRowGroupHidesColumns: false,
                    }),
                    context: {
                      assignedToMe,
                      dateRange,
                      matches,
                    },
                    getRowId: (params) => {
                      if (!params.data) return `past-due-row-${Date.now()}-${Math.floor(Math.random() * 1000)}`
                      if (groupByDate && params.data.aging_bucket && params.data.supplier_id) {
                        return `past-due-${params.data.aging_bucket}-${params.data.supplier_id}`
                      }
                      return `past-due-${params.data.supplier_name || params.data.supplier_id || 'unknown'}`
                    },
                  }}
                />
              </div>

              {/* Total Row */}
              <Horizontal
                style={{
                  background: '#F5F5F5',
                  padding: '12px 16px',
                  borderTop: '2px solid #212529',
                }}
                alignItems='center'
              >
                <Texto weight='bold' style={{ flex: 2, fontSize: '12px', color: '#212529' }}>
                  TOTAL
                </Texto>
                <Texto weight='bold' style={{ width: '80px', textAlign: 'center', fontSize: '12px', color: '#212529' }}>
                  {data?.past_due_overview_total?.bols || 0}
                </Texto>
                <Texto weight='bold' style={{ width: '80px', textAlign: 'center', fontSize: '12px', color: '#212529' }}>
                  {data?.past_due_overview_total?.invoices || 0}
                </Texto>
                <Texto weight='bold' style={{ width: '80px', textAlign: 'center', fontSize: '12px', color: '#212529' }}>
                  {data?.past_due_overview_total?.matched || 0}
                </Texto>
                <Texto weight='bold' style={{ width: '120px', textAlign: 'right', fontSize: '12px', color: '#212529' }}>
                  {formatDollarMetric(data?.past_due_overview_total?.outstanding || 0)}
                </Texto>
              </Horizontal>
            </Vertical>
          </Vertical>
        </Vertical>

        {/* Right Column - Open Summary */}
        <Vertical
          style={{
            flex: 2,
            minWidth: '800px',
            height: '100%',
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.07)',
            overflow: 'visible',
          }}
          gap={0}
        >
          {/* Header Section */}
          <Horizontal
            justifyContent='space-between'
            alignItems='center'
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid #E5E7EB',
              overflow: 'visible',
            }}
          >
            <Horizontal gap={10} alignItems='center'>
              <Texto category='p2' weight='bold'>
                Open Summary
              </Texto>
              <BackgroundRefreshSpinner isRefreshing={isBackgroundRefreshing} />
            </Horizontal>
            <BBDTag style={{ width: 'min-content' }}>
              Past {trendsData?.length || 0} {(trendsData?.length || 0) === 1 ? 'Day' : 'Days'}
            </BBDTag>
          </Horizontal>

          {/* Main Content Area */}
          <Vertical style={{ flex: 1, padding: '12px 24px', gap: '12px', overflow: 'visible' }} gap={24}>
            {/* Metric Cards - Same as Launchpad */}
            <Horizontal gap={16} style={{ overflow: 'visible' }}>
              {/* Auto-Approve Percentage Card */}
              <MetricCard
                title='Auto-Approve Percentage'
                value={
                  data?.auto_approve_percentage === 0 && hasValidTrendData
                    ? 'No data'
                    : `${((data?.auto_approve_percentage || 0) * 100).toFixed(1)}%`
                }
                trend={hasValidTrendData && data?.auto_approve_percentage > 0 ? data?.auto_approve_trend : 0}
                sparkData={sparkData.map((item) => ({
                  value: item.auto_approve,
                  date: item.date,
                }))}
                isLoading={isLoading}
                invertColors={false}
                noTrendData={!hasValidTrendData || data?.auto_approve_percentage === 0}
                compact={true}
              />

              {/* Total Past Due Card */}
              <MetricCard
                title='Total Past Due'
                value={data?.past_due_balance || 0}
                trend={hasValidTrendData ? data?.past_due_balance_trend : 0}
                sparkData={sparkData.map((item) => ({
                  value: item.past_due,
                  date: item.date,
                }))}
                isLoading={isLoading}
                invertColors={true}
                formatValue={(v) => formatDollarMetric(v)}
                noTrendData={!hasValidTrendData}
                compact={true}
              />

              {/* Total Open Items Card */}
              <MetricCard
                title='Total Open Items'
                value={data?.open_items_count || 0}
                trend={hasValidTrendData ? data?.open_items_trend : 0}
                sparkData={sparkData.map((item) => ({
                  value: item.open_items,
                  date: item.date,
                }))}
                isLoading={isLoading}
                invertColors={true}
                formatValue={(v) => formatMetricNumber(v)}
                noTrendData={!hasValidTrendData}
                compact={true}
              />

              {/* Total Unmatched Items Card */}
              <MetricCard
                title='Total Unmatched Items'
                value={data?.total_orphan_bols || 0}
                trend={hasValidTrendData ? data?.orphan_bols_trend : 0}
                sparkData={sparkData.map((item) => ({
                  value: item.orphan_bols,
                  date: item.date,
                }))}
                isLoading={isLoading}
                invertColors={true}
                formatValue={(v) => formatMetricNumber(v)}
                noTrendData={!hasValidTrendData}
                compact={true}
              />
            </Horizontal>

            {/* Status Breakdown */}
            <Vertical
              flex='0'
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                borderBottom: '1px solid #E5E7EB',
                overflow: 'visible',
              }}
            >
              {/* Colored Progress Bar */}
              <Horizontal
                style={{
                  width: '100%',
                  overflow: 'hidden',
                  borderRadius: '6px 6px 0 0',
                }}
              >
                {/* Overdue - Red */}
                <div
                  style={{
                    width: `${statusBarData[0].percentage}%`,
                    height: '57px',
                    background: 'var(--theme-error)',
                    transition: 'all 0.2s',
                  }}
                />
                {/* On Hold - Orange */}
                <div
                  style={{
                    width: `${statusBarData[1].percentage}%`,
                    height: '57px',
                    background: 'var(--theme-warning)',
                    transition: 'all 0.2s',
                  }}
                />
                {/* Open - Solid Blue */}
                <div
                  style={{
                    width: `${statusBarData[2].percentage}%`,
                    height: '57px',
                    background: '#0288D1',
                    transition: 'all 0.2s',
                  }}
                />
              </Horizontal>
              {isLoading ? (
                <Horizontal gap={48} style={{ padding: '24px', overflow: 'visible' }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Vertical key={index} style={{ flex: 1 }}>
                      <Skeleton.Input active style={{ width: '100%', height: 20, marginBottom: 12 }} />
                      <Skeleton active paragraph={{ rows: 5 }} />
                    </Vertical>
                  ))}
                </Horizontal>
              ) : (
                <Horizontal gap={48} style={{ padding: '24px', overflow: 'visible' }}>
                  <StatusAggregation
                    title='Overdue'
                    count={data?.statusBreakdown?.overDue?.count || 0}
                    percentage={statusBarData[0].percentage}
                    topSuppliers={data?.statusBreakdown?.overDue?.topSuppliers || []}
                    showTag={false}
                    showDot={true}
                    dotColor='#DC3545'
                    onHeaderClick={(event) => {
                      const baseUrl = '/supplier_rec/match_view?tab=all_open&invoice_due_filter=overdue'
                      const url = createMatchViewUrl(baseUrl)
                      handleNavigation(url, navigate, event)
                    }}
                    onSupplierClick={(supplierName, event) => {
                      const baseUrl = `/supplier_rec/match_view?tab=all_open&invoice_due_filter=overdue&supplier_name=${encodeURIComponent(supplierName)}`
                      const url = createMatchViewUrl(baseUrl)
                      handleNavigation(url, navigate, event)
                    }}
                  />

                  <StatusAggregation
                    title='On Hold'
                    count={data?.statusBreakdown?.onHold?.count || 0}
                    percentage={statusBarData[1].percentage}
                    topSuppliers={data?.statusBreakdown?.onHold?.topSuppliers || []}
                    showTag={false}
                    showDot={true}
                    dotColor='#FD7E14'
                    onHeaderClick={(event) => {
                      const baseUrl = '/supplier_rec/match_view?tab=on_hold'
                      const url = createMatchViewUrl(baseUrl)
                      handleNavigation(url, navigate, event)
                    }}
                    onSupplierClick={(supplierName, event) => {
                      const baseUrl = `/supplier_rec/match_view?tab=on_hold&supplier_name=${encodeURIComponent(supplierName)}`
                      const url = createMatchViewUrl(baseUrl)
                      handleNavigation(url, navigate, event)
                    }}
                  />

                  <StatusAggregation
                    title='Open'
                    count={data?.statusBreakdown?.open?.count || 0}
                    percentage={statusBarData[2].percentage}
                    topSuppliers={data?.statusBreakdown?.open?.topSuppliers || []}
                    showTag={false}
                    showDot={true}
                    dotColor='#0288D1'
                    onHeaderClick={(event) => {
                      const baseUrl = '/supplier_rec/match_view?tab=all_open'
                      const url = createMatchViewUrl(baseUrl)
                      handleNavigation(url, navigate, event)
                    }}
                    onSupplierClick={(supplierName, event) => {
                      const baseUrl = `/supplier_rec/match_view?tab=all_open&supplier_name=${encodeURIComponent(supplierName)}`
                      const url = createMatchViewUrl(baseUrl)
                      handleNavigation(url, navigate, event)
                    }}
                  />
                </Horizontal>
              )}
            </Vertical>

            {/* Bottom Section - Grids */}
            <Horizontal gap={24} style={{ flex: 1, overflow: 'visible' }}>
              {/* Top 10 Past Due By Supplier */}
              <Vertical
                style={{
                  flex: 1.2,
                  overflow: 'visible',
                }}
                gap={0}
              >
                <Horizontal
                  justifyContent='space-between'
                  alignItems='center'
                  style={{
                    padding: '16px 0px',
                    borderBottom: '1px solid #E5E7EB',
                    overflow: 'visible',
                  }}
                >
                  <Texto category='p2' weight='bold'>
                    Top 10 Past Due By Supplier
                  </Texto>
                </Horizontal>

                <Vertical
                  style={{
                    flex: 1,
                    border: '1px solid #DDDDDD',
                    borderRadius: '0px',
                    overflow: 'visible',
                  }}
                  bg='#FAFAFA'
                >
                  <GraviGrid
                    rowData={data?.top_10_past_due || []}
                    loading={isLoading}
                    agPropOverrides={{
                      columnDefs: top10ColumnDefs,
                      headerHeight: 36,
                      rowHeight: 40,
                      domLayout: 'normal',
                      suppressHorizontalScroll: false,
                      rowGroupPanelShow: 'onlyWhenGrouping',
                      context: {
                        assignedToMe,
                        dateRange,
                        matches,
                      },
                      getRowId: (params) => {
                        if (!params.data) return `top10-row-${Date.now()}-${Math.floor(Math.random() * 1000)}`
                        return `top10-${params.data.supplier_name || params.data.supplier_id || 'unknown'}`
                      },
                    }}
                  />
                </Vertical>

                {/* Total Row */}
                {isLoading ? (
                  <Skeleton.Input active style={{ width: '100%', height: 40 }} />
                ) : (
                  <Horizontal
                    style={{
                      background: '#F5F5F5',
                      padding: '12px 16px',
                      borderTop: '2px solid #212529',
                    }}
                    alignItems='center'
                  >
                    <Texto weight='bold' style={{ flex: 1, fontSize: '12px', color: '#212529' }}>
                      TOTAL
                    </Texto>
                    <Horizontal gap={60}>
                      <Texto style={{ fontSize: '12px', color: '#212529' }}>{data?.top_10_total?.bols || 0}</Texto>
                      <Texto style={{ fontSize: '12px', color: '#212529' }}>{data?.top_10_total?.invoices || 0}</Texto>
                      <Texto weight='bold' style={{ fontSize: '12px', color: '#212529' }}>
                        $
                        {(data?.top_10_total?.amount || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Texto>
                    </Horizontal>
                  </Horizontal>
                )}
              </Vertical>

              {/* Late Payment Summary */}
              <Vertical gap={0} style={{ flex: 1.2, overflow: 'visible' }}>
                <Horizontal
                  justifyContent='space-between'
                  alignItems='center'
                  style={{
                    padding: '16px 0px',
                    borderBottom: '1px solid #E5E7EB',
                    overflow: 'visible',
                  }}
                >
                  <Texto category='p2' weight='bold'>
                    Late Payment Summary
                  </Texto>
                </Horizontal>

                <Vertical
                  style={{
                    flex: 1,
                    border: '1px solid #DDDDDD',
                    borderRadius: '0px',
                    overflow: 'visible',
                  }}
                  bg='#FAFAFA'
                >
                  <GraviGrid
                    rowData={data?.late_payment_summary || []}
                    loading={isLoading}
                    agPropOverrides={{
                      columnDefs: latePaymentColumnDefs,
                      headerHeight: 36,
                      rowHeight: 40,
                      domLayout: 'normal',
                      suppressHorizontalScroll: false,
                      rowGroupPanelShow: 'onlyWhenGrouping',
                      context: {
                        assignedToMe,
                        dateRange,
                        matches,
                      },
                      getRowId: (params) => {
                        if (!params.data) return `row-${Date.now()}-${Math.floor(Math.random() * 1000)}`
                        const invoiceNum = params.data.invoice_number || 'unknown'
                        const supplierName = params.data.supplier_name || ''
                        // Use a combination of invoice and supplier to ensure uniqueness
                        return `late-payment-${invoiceNum}-${supplierName}`
                      },
                    }}
                  />
                </Vertical>

                {/* Total Row */}
                {isLoading ? (
                  <Skeleton.Input active style={{ width: '100%', height: 40 }} />
                ) : (
                  <Horizontal
                    style={{
                      background: '#F5F5F5',
                      padding: '12px 16px',
                      borderTop: '2px solid #212529',
                    }}
                    alignItems='center'
                  >
                    <Texto weight='bold' style={{ flex: 1, fontSize: '12px', color: '#212529' }}>
                      TOTAL
                    </Texto>
                    <Texto
                      weight='bold'
                      style={{ width: '120px', textAlign: 'right', fontSize: '12px', color: '#212529' }}
                    >
                      $
                      {(data?.late_payment_total || 5800.0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Texto>
                  </Horizontal>
                )}
              </Vertical>
            </Horizontal>
          </Vertical>
        </Vertical>
      </Horizontal>
    </>
  )
}

export default Management
